import { Box, IconButton, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useState } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { camelCase } from '@/utils/stringCaser';
import { REGEX } from '@/constants/regex';
import { buildInputRulesObject } from '@/utils/buildInputRulesObject';

type SVPasswordInputProps = {
  name: string;
  label: string;
  disabled?: boolean;
  passwordName?: string;
  bypassValidation?: boolean;
};

export default function SVPasswordInput({
  disabled = false,
  label,
  name,
  passwordName = '',
  bypassValidation,
}: SVPasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const methods = useFormContext();
  const passwordValue = passwordName ? methods.watch(passwordName) : '';
  const builtRulesObject = buildInputRulesObject(label, {
    required: true,
    minLength: 10,
    validate: (val: string) => {
      if (bypassValidation) {
        return;
      }

      if (!REGEX.CHECK_NUMBER.test(val)) {
        return 'Password must contain at least one number';
      }
      if (!REGEX.CHECK_UPPERCASE.test(val)) {
        return 'Password must contain at least one uppercase character';
      }
      if (!REGEX.CHECK_LOWERCASE.test(val)) {
        return 'Password must contain at least one lowercase character';
      }
      if (!REGEX.CHECK_SYMBOL.test(val)) {
        return 'Password must contain at least one special character';
      }
      if (passwordName && val !== passwordValue) {
        return 'Password and Confirm Password must be identical';
      }
      return;
    },
  });

  return (
    <Controller
      control={methods.control}
      name={name}
      rules={builtRulesObject}
      render={({
        field: { value, onChange, onBlur, ref },
        fieldState: { error },
      }) => (
        <Box sx={{ marginBottom: 2 }}>
          <TextField
            disabled={disabled}
            error={!!error?.message}
            id={camelCase(label)}
            slotProps={{
              input: {
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{ minWidth: 'fit-content' }}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                ),
              },
            }}
            inputRef={ref}
            label={label}
            onChange={e => {
              onChange(e);
            }}
            onBlur={onBlur}
            type={showPassword ? 'text' : 'password'}
            value={value || ''}
            sx={{ width: '100%' }}
            helperText={error?.message}
          />
        </Box>
      )}
    />
  );
}
